import { useMemo } from 'react';

import { computeMarkLevels } from '../utils';

import type { Mark, SliderProps } from '../types';

interface UseSliderMarksParams {
  marks: Mark[];
  max: number;
  min: number;
  orientation: SliderProps['orientation'];
}

interface UseSliderMarksResult {
  hasStaggeredLabels: boolean;
  markLevels: number[];
  markRenderKeys: string[];
}

/*
 * Computes derived data for rendering slider marks:
 * - **markLevels** — the stagger level (0, 1, or 2) for each rendered mark by index.
 * - **markRenderKeys** — stable React keys for each mark element, handling duplicate values.
 * - **hasStaggeredLabels** — whether any label was pushed to a non-zero level,
 *   which triggers additional CSS spacing on the slider.
 *
 * Accepts `{ marks, min, max, orientation }` — the resolved mark array and slider range.
 * Returns precomputed mark rendering data.
 */
export const useSliderMarks = ({ marks, max, min, orientation }: UseSliderMarksParams): UseSliderMarksResult => {
  const marksWithLevels = useMemo(() => {
    const indexedMarks = marks.map((mark, index) => ({ ...mark, index }));

    if (orientation !== 'horizontal') {
      return indexedMarks.filter((mark) => mark.label != null).map((mark) => ({ ...mark, level: 0 }));
    }

    return computeMarkLevels(indexedMarks, min, max);
  }, [marks, max, min, orientation]);

  const markLevels = useMemo(() => {
    const levels = Array.from({ length: marks.length }, () => 0);

    for (const mark of marksWithLevels) {
      levels[mark.index] = mark.level;
    }

    return levels;
  }, [marks.length, marksWithLevels]);

  // Generate unique React keys for each mark. When duplicate value+label
  // combinations exist, append an occurrence suffix to keep keys unique.
  const markRenderKeys = useMemo(() => {
    const occurrences = new Map<string, number>();

    return marks.map((mark) => {
      const base = `${mark.value}-${String(mark.label ?? '')}`;
      const occurrence = occurrences.get(base) ?? 0;
      occurrences.set(base, occurrence + 1);
      return occurrence === 0 ? base : `${base}-${occurrence}`;
    });
  }, [marks]);

  const hasStaggeredLabels = orientation === 'horizontal' && marksWithLevels.some((m) => m.level > 0);

  return { hasStaggeredLabels, markLevels, markRenderKeys };
};

export default useSliderMarks;
